import React, { useState, useCallback, useReducer, useMemo } from 'react';

import TabHeader from 'components/partials/tab-header';
import TableComponent from 'components/partials/table-component/table-component';
import type { TableColumnDef } from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import Loader from 'components/partials/loader/loader';

import AidStudioFilterForm from './aid-studio-filter-form';
import { getAidStudioTableSettings } from './aid-studio-table-settings';
import { getStatusFilterValue } from './aid-studio.constants';

import { useAidModelsList } from './hooks/use-aid-models';
import { useCurrentUser } from 'state/current-user/current-user.thunk';

import { usersReducer } from 'containers/admin/reducer';
import {
  SEARCH_CHANGE,
  FILTER_CHANGE,
  PAGE_CHANGE,
} from 'containers/admin/actions';

import Icons from 'assets/icons';

const initialState = {
  search: '',
  pageIndex: 0,
};

interface AidStudioTabProps {
  clientId: string | number;
}

interface AidModelsListResponse {
  data?: unknown[];
  pagination?: { pageSize: number; totalRecords: number };
}

const AidStudioTab = ({
  clientId,
}: AidStudioTabProps): React.JSX.Element => {
  const currentUser = useCurrentUser() as unknown as {
    data?: { oktaUserId?: string };
  };
  const userId = currentUser?.data?.oktaUserId;

  const [state, dispatch] = useReducer(usersReducer, initialState);
  const [filterStatus, setFilterStatus] = useState('all');

  const [{ pageSize, totalRecords }, setAmount] = useState({
    pageSize: 10,
    totalRecords: 0,
  });

  const [sortingParams, setSortingParams] = useState([
    { id: 'name', desc: false },
  ]);

  const { search, pageIndex } = state as {
    search: string;
    pageIndex: number;
  };

  const queryParams = useMemo(() => {
    const statusValues = getStatusFilterValue(filterStatus);

    return {
      pageIndex,
      pageSize,
      sortBy: sortingParams,
      filters: {
        name: search ? { contains: search } : undefined,
        status: statusValues ? { in: statusValues } : undefined,
      },
    };
  }, [pageIndex, pageSize, sortingParams, search, filterStatus]);

  const aidModelsQuery = useAidModelsList({
    userId: userId ?? '',
    clientId,
    queryParams: queryParams as never,
    enabled: !!userId && !!clientId,
  });
  const response = aidModelsQuery.data as AidModelsListResponse | undefined;
  const isLoading = aidModelsQuery.isLoading;
  const isFetching = aidModelsQuery.isFetching;
  const isError = aidModelsQuery.isError;

  const data = response?.data || [];
  const paginationData = response?.pagination;

  React.useEffect(() => {
    if (paginationData) {
      setAmount({
        pageSize: paginationData.pageSize,
        totalRecords: paginationData.totalRecords,
      });
    }
  }, [paginationData]);

  const handleSearch = useCallback(
    (payload: string): void => dispatch({ type: SEARCH_CHANGE, payload }),
    []
  );

  const handleFilter = useCallback((value: string | undefined): void => {
    dispatch({ type: FILTER_CHANGE });
    setFilterStatus(value ?? 'all');
  }, []);

  const handlePagination = useCallback(
    (payload: number): void => dispatch({ type: PAGE_CHANGE, payload }),
    []
  );

  const columns = useMemo<TableColumnDef<unknown>[]>(
    () => getAidStudioTableSettings(clientId) as TableColumnDef<unknown>[],
    [clientId]
  );

  const initialLoading = isLoading && !response;
  const tableLoading = isFetching && !initialLoading;

  const renderContent = (): React.JSX.Element => {
    if (initialLoading) {
      return (
        <Loader
          loading={true}
          customClass="!bg-white"
          fullScreen={false}
          overlayClassName="!opacity-0"
        />
      );
    }

    if (!!data.length || tableLoading) {
      return (
        <TableComponent
          columns={columns}
          data={data}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPagination={handlePagination}
          setAmount={setAmount}
          sortingParams={sortingParams}
          setSortingParams={setSortingParams}
          totalRecords={totalRecords}
          isLoading={tableLoading}
          customHeight="max-h-[50vh]"
        />
      );
    }

    if (isError) {
      return (
        <EmptySpaceContent
          customClass="!mt-14"
          image={<Icons.EmptyImageAll customClass="w-[210px] h-[210px]" />}
          text="An error occurred while loading data. Please try again."
        />
      );
    }

    return (
      <EmptySpaceContent
        customClass="!mt-14"
        image={<Icons.EmptyImageAll customClass="w-[210px] h-[210px]" />}
        text={
          filterStatus === 'all' && !search
            ? 'There are no aid models yet.'
            : 'Sorry, no results found. Please try a different keyword or filter.'
        }
      />
    );
  };

  return (
    <div>
      <TabHeader
        title="Aid studio"
        search={search}
        onSearchChange={handleSearch}
        actionButton={{
          label: 'Add new',
          to: `/admin/clients/${clientId}/aid-studio/new`,
          testId: 'add-aid-model-button',
        }}
        customClass="!mb-0"
      />

      <AidStudioFilterForm status={filterStatus} onClick={handleFilter} />
      {renderContent()}
    </div>
  );
};

export default AidStudioTab;
